import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Editor from '../../elements/richText/RichText';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Skeleton } from "@ann/web-components";
import { generateDocx, generatePdf } from './DocumentUtils';
import './DocumentCanvas.styles.css';
import ConfirmationModal from '../../elements/v2/modals/ConfirmationModal';
import WarningIcon from "../../elements/icon/assets/warning.svg";
import SaveToVaultModal from './modals/SaveToVaultModal';
import { useStore } from '../../hooks';
import Alerts from '../../elements/v2/Alerts/Alerts';
import AICopilotToolbar from './components/AICopilotToolbar';
import PendingChangesIndicator from './components/PendingChangesIndicator';
import InlineDiffView from './components/InlineDiffView';
import DAGProgressIndicator from './components/DAGProgressIndicator';
import TextSelectionMenu from './components/TextSelectionMenu';
import { useCollaborativeEdit } from '../../hooks/useCollaborativeEdit';
import { createDocumentByTenant, attachDocumentbyTenant } from '../../services/document';

/**
 * DocumentCanvas - An isolated, reusable component for document editing with AI collaboration
 *
 * @component
 */
const DocumentCanvas = ({
  initialContent = '',
  documentTitle = 'Document',
  onClose,
  onChange,
  className = '',
  borderRadius = '8px',
  headerBgColor = '#F5F7FA',
  footerBgColor = '#FFFFFF',
  editorClassName = '',
  downloadOptions = ['pdf', 'docx'],
  onDownload,
  exitButtonText = 'Exit',
  downloadButtonText = 'Download',
  agentUuid = null,
  vaultUuid = null,
  enableAICopilot = true, // New prop to enable/disable AI copilot
  skipExitConfirmation = false, // New prop to skip exit confirmation (for auto-saved documents)
  onDiffViewToggle, // Callback to notify parent about diff view state
  onAddChatMessage, // NEW: Callback to add messages to chat
}) => {
  const { authStore, fileStore, playgroundAgentStore, playgroundConfigStore, conversationsStore } = useStore();
  const [editorState, setEditorState] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSaveToVaultModal, setShowSaveToVaultModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [copilotEnabled, setCopilotEnabled] = useState(true); // Start with copilot enabled by default
  const [documentContent, setDocumentContent] = useState(initialContent);
  const [showDiffInEditor, setShowDiffInEditor] = useState(false); // Toggle for inline diff view
  const downloadMenuRef = useRef(null);
  const downloadButtonRef = useRef(null);
  const editorContainerRef = useRef(null); // Reference to editor container for text selection
  
  // Collaborative editing hook
  const {
    isStreaming,
    isThinking,
    pendingChanges,
    streamedContent,
    error: copilotError,
    requestEdit,
    stopStreaming,
    confirmChange,
    rejectChange,
    undoLastChange,
    isActive: isCopilotActive,
    hasChanges: hasPendingChanges,
    // NEW: DAG Progress
    dagProgress,
    currentDagNode,
  } = useCollaborativeEdit({
    agentId: agentUuid,
    documentContent: documentContent,
    conversationId: conversationsStore.conversationId,
    threadId: conversationsStore.threadId,
    onContentUpdate: (newContent) => {
      console.log('🔄 Content update received:', {
        newContentLength: newContent?.length,
        currentContentLength: documentContent?.length,
        newContent: newContent?.substring(0, 100) + '...'
      });
      console.log('📝 BEFORE update - documentContent:', documentContent?.substring(0, 100));
      setDocumentContent(newContent);
      console.log('📝 AFTER update - setDocumentContent called with:', newContent?.substring(0, 100));
      // Update editor state if needed
      const mockEditorState = {
        toText: () => newContent,
        toHTML: () => `<p>${newContent}</p>`
      };
      setEditorState(mockEditorState);
      setHasChanges(true);
      if (onChange) {
        onChange(mockEditorState);
      }
    },
  });

  // Wrapper functions to handle UI state
  const handleConfirmChange = (changeId) => {
    confirmChange(changeId);
    setShowDiffInEditor(false);
    if (onDiffViewToggle) onDiffViewToggle(false);
  };

  const handleRejectChange = (changeId) => {
    rejectChange(changeId);
    setShowDiffInEditor(false);
    if (onDiffViewToggle) onDiffViewToggle(false);
  };

  // Handler for text selection actions
  // When text is selected in canvas, send to reasoning agent via requestEdit (updates canvas)
  // Only send the selected text, not the entire document (like chat behavior)
  const handleTextSelectionAction = (instruction, selectedText) => {
    console.log('📝 Canvas text selection action:', { 
      instruction: instruction?.substring(0, 100), 
      selectedText: selectedText?.substring(0, 100) 
    });
    // Pass selectedText flag to requestEdit so it doesn't add the full document
    requestEdit(instruction, { skipFullDocument: true, selectedText });
  };

  // Notify parent when diff view state changes
  useEffect(() => {
    if (onDiffViewToggle) {
      onDiffViewToggle(showDiffInEditor);
    }
  }, [showDiffInEditor, onDiffViewToggle]);
  
  const handleContentChange = (editorState) => {
    setEditorState(editorState);
    setHasChanges(true);
    
    // Extract text content from editor state
    if (editorState && editorState.toText) {
      const newText = editorState.toText();
      console.log('📝 Editor changed - updating documentContent:', {
        newLength: newText?.length,
        preview: newText?.substring(0, 100)
      });
      setDocumentContent(newText);
    }
    
    if (onChange) {
      onChange(editorState);
    }
  };
  
  // Initialize editorState when component mounts or initialContent changes
  useEffect(() => {
    if (initialContent && !editorState) {
      // Create a mock editorState object for initial content
      const mockEditorState = {
        toText: () => initialContent,
        toHTML: () => `<p>${initialContent}</p>`
      };
      setEditorState(mockEditorState);
      setDocumentContent(initialContent);
    }
  }, [initialContent, editorState]);
  
  // Show error alerts for copilot
  useEffect(() => {
    if (copilotError) {
      console.error('Copilot error:', copilotError);
    }
  }, [copilotError]);
  
  const toggleDownloadMenu = (e) => {
    e.stopPropagation(); // Prevent event from bubbling
    setShowDownloadMenu(prevState => !prevState);
  };

  // Helper function to upload to a single vault/folder
  const uploadToVaultFolder = async (file, destination) => {
    const { vaultUuid, folderUuid, vaultName, folderName } = destination;
    
    console.log(`🚀 Starting upload to ${vaultName}/${folderName || 'Root'}`);

    // Step 1: Create document entry (USING TENANT ENDPOINT)
    const createDocumentBody = {
      name: file.name,
      vaultUuid,
      folderUuid: folderUuid || null,
      type: file.type || 'application/octet-stream',
      url: '',
      metadata: {
        source: "document_canvas",
        version: "1.0",
        region: "us-east-1",
        confidential: true
      }
    };

    console.log('📝 Step 1: Creating document entry...');
    const createDocumentResponse = await createDocumentByTenant(createDocumentBody);
    
    // Access nested response structure correctly
    const { status_code, body } = createDocumentResponse.data;
    
    if (status_code < 200 || status_code >= 300) {
      throw new Error('Failed to create document entry');
    }
    
    const documentUuid = body.document.document_uuid;
    const versionUuid = body.version.doc_version_uuid;
    console.log('✅ Document created:', documentUuid);

    // Step 2: Assign document to agent (OPTIONAL - only if agentUuid is provided)
    if (agentUuid) {
      console.log('📎 Step 2: Assigning to agent (optional)...');
      try {
        const assignDocumentToAgentBody = {
          document_uuid: documentUuid,
          agent_uuid: agentUuid,
          expires_at: "2026-12-31 23:59:59",
          owner: authStore.userId,
          created_by: authStore.userId,
          granted_by: authStore.userId,
        };

        await playgroundAgentStore.assignDocumentAgent(assignDocumentToAgentBody);
        console.log('✅ Agent assignment successful');
      } catch (error) {
        console.warn('⚠️ Agent assignment failed (continuing anyway):', error);
        // Continue without agent assignment - document still saves to drive
      }
    } else {
      console.log('⏭️ Step 2: Skipping agent assignment (no agentUuid)');
    }

    // Step 3: Create file entry and get upload URL
    console.log('📄 Step 3: Creating file entry and getting upload URL...');
    const createFileBody = {
      name: file.name,
      folder_id: folderUuid || '-',
      tenant_id: authStore.selectedAccount?.tenantId || authStore.selectedAccount?.id,
      user_id: authStore.userId,
      file_type: file.type || 'application/octet-stream',
      status: 'active',
      vault_uuid: vaultUuid,
      document_uuid: documentUuid,
      version_uuid: versionUuid
    };

    const createFileResponse = await fileStore.createFile(createFileBody);
    console.log('✅ File entry created, response:', createFileResponse);

    if (!createFileResponse?.body?.uploadUrl) {
      console.error('❌ No upload URL in response:', createFileResponse);
      throw new Error('No upload URL received');
    }

    const uploadUrl = createFileResponse.body.uploadUrl;
    const s3Key = createFileResponse.body.fileKey;
    console.log('🔗 Upload URL received, S3 key:', s3Key);

    // Step 4: Upload file to S3
    console.log('☁️ Step 4: Uploading file to S3...');
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      }
    });

    if (!uploadResponse.ok) {
      console.error('❌ S3 upload failed:', uploadResponse.status, uploadResponse.statusText);
      throw new Error(`Upload failed: ${uploadResponse.status}`);
    }
    
    console.log('✅ File uploaded to S3 successfully');

    // Step 5: Attach document (USING TENANT ENDPOINT)
    console.log('🔗 Step 5: Attaching document...');
    const attachDocumentBody = {
      s3_key: s3Key,
      document_uuid: documentUuid,
    };

    await attachDocumentbyTenant(attachDocumentBody);
    console.log('✅ Document attached successfully');
    console.log(`🎉 Upload complete to ${vaultName}/${folderName || 'Root'}`);
  };

  const handleSaveToVault = async (fileName, fileType, destinations = []) => {
    setIsGenerating(true);
    try {
      // Get vault UUID from playgroundConfigStore (same as playground does)
      const effectiveVaultUuid = vaultUuid || playgroundConfigStore.agentStore.vaultUuid;
      
      // Validate agent and vault UUIDs
      if (!agentUuid) {
        throw new Error("No agent selected. Please select an agent to save to their vault.");
      }

      if (!effectiveVaultUuid) {
        throw new Error("Agent does not have an associated vault.");
      }

      // Get content from editorState or fall back to documentContent
      let contentToSave = '';
      
      if (editorState && editorState.toText) {
        contentToSave = editorState.toText();
      } else if (documentContent) {
        contentToSave = documentContent;
      } else {
        throw new Error("No content available to save");
      }
      
      if (!contentToSave || contentToSave.trim() === '') {
        throw new Error("Document is empty");
      }

      // Handle different file types
      let fileBlob;
      let fullFileName;
      
      switch (fileType.value) {
        case 'txt':
          fileBlob = new Blob([contentToSave], { type: fileType.mimeType });
          fullFileName = `${fileName}.txt`;
          break;
          
        case 'md':
          fileBlob = new Blob([contentToSave], { type: fileType.mimeType });
          fullFileName = `${fileName}.md`;
          break;
          
        case 'html':
          // Convert to HTML if editorState has toHTML method, otherwise wrap in basic HTML
          let htmlContent;
          if (editorState && editorState.toHTML) {
            htmlContent = `<!DOCTYPE html><html><head><title>${fileName}</title></head><body>${editorState.toHTML()}</body></html>`;
          } else {
            htmlContent = `<!DOCTYPE html><html><head><title>${fileName}</title></head><body><pre>${contentToSave}</pre></body></html>`;
          }
          fileBlob = new Blob([htmlContent], { type: fileType.mimeType });
          fullFileName = `${fileName}.html`;
          break;
          
        case 'pdf':
          // For PDF, generate the blob instead of downloading
          try {
            // Create PDF using jsPDF
            const { jsPDF } = await import('jspdf');
            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'mm',
              format: 'a4'
            });
            
            // Add document title
            pdf.setFontSize(18);
            pdf.setTextColor(0, 0, 0);
            pdf.text(fileName, 20, 20);
            
            // Add a line under the title
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.5);
            pdf.line(20, 24, 190, 24);
            
            // Add content with text wrapping
            pdf.setFontSize(12);
            const paragraphs = contentToSave.split(/\r?\n/);
            let yPosition = 30;
            const lineHeight = 7;
            const pageWidth = 170;
            
            paragraphs.forEach(paragraph => {
              if (paragraph.trim() === '') {
                yPosition += lineHeight / 2;
                return;
              }
              
              // Check if we need a new page
              if (yPosition > 270) {
                pdf.addPage();
                yPosition = 20;
              }
              
              const lines = pdf.splitTextToSize(paragraph, pageWidth);
              pdf.text(lines, 20, yPosition);
              yPosition += lines.length * lineHeight + 2;
            });
            
            // Get PDF as blob instead of downloading
            fileBlob = pdf.output('blob');
            fullFileName = `${fileName}.pdf`;
          } catch (error) {
            throw new Error(`Failed to generate PDF: ${error.message}`);
          }
          break;
          
        case 'docx':
          // For DOCX, generate the blob instead of downloading
          try {
            const { Document, Packer, Paragraph, TextRun } = await import('docx');
            
            // Split text content by line breaks
            const paragraphs = contentToSave.split(/\r?\n/).filter(line => line.trim() !== '');
            
            // Create paragraphs for the docx document
            const docxParagraphs = paragraphs.map(paragraph => 
              new Paragraph({
                children: [
                  new TextRun(paragraph)
                ]
              })
            );
            
            // Create a new Document
            const doc = new Document({
              sections: [
                {
                  properties: {},
                  children: docxParagraphs
                }
              ]
            });
            
            // Generate the docx blob
            fileBlob = await Packer.toBlob(doc);
            fullFileName = `${fileName}.docx`;
          } catch (error) {
            throw new Error(`Failed to generate DOCX: ${error.message}`);
          }
          break;
          
        default:
          fileBlob = new Blob([contentToSave], { type: 'text/plain' });
          fullFileName = `${fileName}.txt`;
      }

      const file = new File([fileBlob], fullFileName, { type: fileType.mimeType });

      // Check auth state
      if (!authStore.userId) {
        throw new Error("No user ID found");
      }

      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        throw new Error('File too large. Maximum size is 100MB.');
      }

      // Upload to each selected destination
      let successCount = 0;
      const errors = [];

      for (const destination of destinations) {
        try {
          await uploadToVaultFolder(file, destination);
          successCount++;
        } catch (error) {
          console.error(`Error uploading to ${destination.vaultName}/${destination.folderName}:`, error);
          errors.push(`${destination.vaultName}/${destination.folderName}: ${error.message}`);
        }
      }

      // Show results
      if (successCount > 0) {
        const message = successCount === destinations.length
          ? `Document successfully saved to ${successCount} location${successCount > 1 ? 's' : ''}!`
          : `Document saved to ${successCount} of ${destinations.length} locations. Some uploads failed.`;
        
        setSuccessMessage(message);
      setShowSuccessAlert(true);
      } else {
        throw new Error(`Failed to save to any location:\n${errors.join('\n')}`);
      }
    } catch (error) {
      console.error('Error saving document to vault:', error);
      alert(`Error saving document: ${error.message}`);
    } finally {
      setIsGenerating(false);
      setShowSaveToVaultModal(false);
    }
  };

  const handleDownload = (format) => {
    // Set loading state
    setIsGenerating(true);
    
    if (onDownload) {
      onDownload(format, editorState, documentTitle)
        .then(() => {
          setIsGenerating(false);
        })
        .catch(error => {
          setIsGenerating(false);
        });
    } else {
      // Default download behavior
      try {
        if (format === 'pdf') {
          generatePdf(editorState, documentTitle)
            .then(() => {
              setIsGenerating(false);
            })
            .catch(error => {
              setIsGenerating(false);
            });
        } else if (format === 'docx') {
          generateDocx(editorState, documentTitle)
            .then(() => {
              setIsGenerating(false);
            })
            .catch(error => {
              setIsGenerating(false);
            });
        }
      } catch (error) {
        setIsGenerating(false);
      }
    }
    setShowDownloadMenu(false);
  };

  const handleExitClick = () => {
    // Skip confirmation if auto-save is enabled
    if (skipExitConfirmation) {
      if (onClose) onClose();
      return;
    }
    
    // Only show the confirmation if there are changes
    if (hasChanges) {
      setShowExitConfirmation(true);
    } else {
      if (onClose) onClose();
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    if (onClose) onClose();
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
  };
  
  // Handle outside clicks for the download menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close the menu if it's open and the click is outside both the button and menu
      if (
        showDownloadMenu &&
        downloadButtonRef.current && 
        !downloadButtonRef.current.contains(event.target) &&
        event.target.closest('.download-menu') === null
      ) {
        setShowDownloadMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadMenu]);

  // Set dynamic styles based on props
  const containerStyle = {
    borderRadius: borderRadius,
  };

  const headerStyle = {
    backgroundColor: headerBgColor,
    borderTopLeftRadius: borderRadius,
    borderTopRightRadius: borderRadius,
  };

  const footerStyle = {
    backgroundColor: footerBgColor,
    borderBottomLeftRadius: borderRadius,
    borderBottomRightRadius: borderRadius,
  };

  return (
    <>
      {showSuccessAlert && (
        <Alerts
          severity="success"
          title="Document uploaded successfully"
          message={successMessage}
          onClose={() => setShowSuccessAlert(false)}
        />
      )}
      
      {copilotError && (
        <Alerts
          severity="error"
          title="AI Copilot Error"
          message={copilotError}
          onClose={() => {}}
        />
      )}

      <div 
        className={`document-canvas ${className} ${showDiffInEditor ? 'showing-inline-diff' : ''}`} 
        style={containerStyle}
      >
        <div className="document-header" style={headerStyle}>
          <h3 className="document-title">{documentTitle}</h3>
          <div className="header-actions">
            {enableAICopilot && agentUuid && (
              <FormControlLabel
                control={
                  <Switch
                    checked={copilotEnabled}
                    onChange={(e) => setCopilotEnabled(e.target.checked)}
                    size="small"
                    icon={<SmartToyIcon />}
                  />
                }
                label="AI Copilot"
                className="copilot-toggle"
              />
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={handleExitClick}
              startIcon={<CloseIcon />}
              className="exit-button"
              data-testid="document-exit-button"
            >
              {exitButtonText}
            </Button>
          </div>
        </div>
        
        {/* AI Copilot UI */}
        {enableAICopilot && copilotEnabled && agentUuid && (
          <div className="copilot-section">
            <AICopilotToolbar
              isActive={isCopilotActive}
              isStreaming={isStreaming}
              onRequestEdit={requestEdit}
              onStopStreaming={stopStreaming}
              onUndo={undoLastChange}
              pendingChangesCount={pendingChanges.length}
              disabled={isGenerating}
            />
            <DAGProgressIndicator
              dagProgress={dagProgress}
              currentDagNode={currentDagNode}
              isVisible={dagProgress.length > 0 || isThinking || isCopilotActive}
              isComplete={!isThinking && !isStreaming && dagProgress.length > 0}
            />
            <PendingChangesIndicator
              pendingChanges={pendingChanges}
              onConfirm={handleConfirmChange}
              onReject={handleRejectChange}
              streamedContent={streamedContent}
              isStreaming={isStreaming}
              onToggleDiff={() => setShowDiffInEditor(!showDiffInEditor)}
              isDiffVisible={showDiffInEditor}
            />
          </div>
        )}
        
        <div className={`document-editor ${editorClassName}`} ref={editorContainerRef}>
          {/* Text selection menu for AI actions */}
          {enableAICopilot && copilotEnabled && (
            <TextSelectionMenu
              onSendToAgent={handleTextSelectionAction}
              containerRef={editorContainerRef}
            />
          )}

          {isThinking ? (
            <div style={{ padding: '40px' }}>
              <Skeleton height="40px" width="60%" style={{ marginBottom: '24px' }} />
              <Skeleton height="20px" width="100%" style={{ marginBottom: '12px' }} />
              <Skeleton height="20px" width="100%" style={{ marginBottom: '12px' }} />
              <Skeleton height="20px" width="90%" style={{ marginBottom: '12px' }} />
              <Skeleton height="20px" width="95%" style={{ marginBottom: '12px' }} />
              <Skeleton height="20px" width="80%" style={{ marginBottom: '24px' }} />
              <Skeleton height="20px" width="100%" style={{ marginBottom: '12px' }} />
              <Skeleton height="20px" width="100%" style={{ marginBottom: '12px' }} />
              <Skeleton height="20px" width="70%" />
            </div>
          ) : showDiffInEditor && pendingChanges.length > 0 && pendingChanges[0].oldContent ? (
            <InlineDiffView
              oldContent={pendingChanges[0].oldContent}
              newContent={pendingChanges[0].content}
            />
          ) : (
            <Editor
              initialText={documentContent}
              onChange={handleContentChange}
            />
          )}
        </div>
        
        <div 
          className="document-footer"
          style={footerStyle}
        >
          <Button
            variant="contained"
            onClick={() => setShowSaveToVaultModal(true)}
            className="save-button"
            disabled={isGenerating || isCopilotActive}
          >
            {isGenerating ? 'Saving to Drive...' : 'Save to Drive'}
          </Button>
          <div className="download-menu-container" ref={downloadMenuRef}>
            {showDownloadMenu && (
              <div className="download-menu">
                {downloadOptions.includes('pdf') && (
                  <button className="download-menu-item" onClick={() => handleDownload('pdf')}>
                    <PictureAsPdfIcon fontSize="small" />
                    PDF
                  </button>
                )}
                {downloadOptions.includes('docx') && (
                  <button className="download-menu-item" onClick={() => handleDownload('docx')}>
                    <InsertDriveFileIcon fontSize="small" />
                    DOCX
                  </button>
                )}
              </div>
            )}
            <Button
              ref={downloadButtonRef}
              variant="contained"
              onClick={toggleDownloadMenu}
              endIcon={<ArrowDropDownIcon />}
              startIcon={<DownloadIcon />}
              className="download-button"
              disabled={isGenerating || isCopilotActive}
            >
              {isGenerating ? 'Generating...' : downloadButtonText}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showExitConfirmation}
        onClose={handleCancelExit}
        onConfirm={handleConfirmExit}
        icon={WarningIcon}
        title="Exit without downloading?"
        message="Your document will be lost unless you download it."
        onConfirmLabel="Exit"
        cancelLabel="Cancel"
        dataTestId="exit-confirmation-modal"
        isExtended={true}
      />

      <SaveToVaultModal
        isOpen={showSaveToVaultModal}
        onClose={() => setShowSaveToVaultModal(false)}
        onSave={handleSaveToVault}
        documentTitle={documentTitle}
      />
    </>
  );
};

DocumentCanvas.propTypes = {
  /** Initial content for the document editor */
  initialContent: PropTypes.string,
  
  /** Title displayed in the document header */
  documentTitle: PropTypes.string,
  
  /** Callback when editor changes */
  onChange: PropTypes.func,
  
  /** Callback when close button is clicked */
  onClose: PropTypes.func.isRequired,
  
  /** Additional CSS class names */
  className: PropTypes.string,
  
  /** Border radius for the canvas container */
  borderRadius: PropTypes.string,
  
  /** Background color for the header */
  headerBgColor: PropTypes.string,
  
  /** Background color for the footer */
  footerBgColor: PropTypes.string,
  
  /** CSS class for the editor */
  editorClassName: PropTypes.string,
  
  /** Available download formats */
  downloadOptions: PropTypes.arrayOf(PropTypes.oneOf(['pdf', 'docx'])),
  
  /** Custom download handler */
  onDownload: PropTypes.func,
  
  /** Text for the exit button */
  exitButtonText: PropTypes.string,
  
  /** Text for the download button */
  downloadButtonText: PropTypes.string,
  
  /** UUID of the agent to save documents to */
  agentUuid: PropTypes.string,
  
  /** UUID of the vault associated with the agent */
  vaultUuid: PropTypes.string,
  
  /** Enable/disable AI copilot feature */
  enableAICopilot: PropTypes.bool,
  
  /** Callback to add messages to chat */
  onAddChatMessage: PropTypes.func,
};

export default DocumentCanvas;

